import { useState } from 'react';
import {
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { CheckCircle, ChevronDown } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { TicketStatus, TicketResolution } from '@duke-hq/svc-api-db';

type DisplayStatus = TicketStatus | TicketResolution;

type Ticket = {
  status: TicketStatus;
  resolution?: TicketResolution | null;
};

type Props = {
  ticket: Ticket;
  onStatusChange: (params: { status: TicketStatus; resolution?: TicketResolution }) => void;
  onMarkResolved: () => void;
  isLoading?: boolean;
};

const STATUS_LABELS = {
  [TicketStatus.NEW]: 'New',
  [TicketStatus.OPEN]: 'Open',
  [TicketStatus.PENDING]: 'Pending',
  [TicketStatus.ON_HOLD]: 'On Hold',
  [TicketResolution.SOLVED]: 'Solved',
  [TicketResolution.REJECTED]: 'Rejected',
} as const;

const STATUS_OPTIONS: DisplayStatus[] = [
  TicketStatus.NEW,
  TicketStatus.OPEN,
  TicketStatus.PENDING,
  TicketStatus.ON_HOLD,
  TicketResolution.SOLVED,
  TicketResolution.REJECTED,
];

function statusToMutatePayload(displayStatus: DisplayStatus) {
  if (displayStatus === TicketResolution.SOLVED) {
    return { status: TicketStatus.CLOSED, resolution: TicketResolution.SOLVED };
  }

  if (displayStatus === TicketResolution.REJECTED) {
    return {
      status: TicketStatus.CLOSED,
      resolution: TicketResolution.REJECTED,
    };
  }

  return { status: displayStatus };
}

export function TicketStatusBar({
  ticket,
  onStatusChange,
  onMarkResolved,
  isLoading = false,
}: Props) {
  const displayStatus = ticket.resolution ?? ticket.status;
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const handleStatusChange = (newStatus: DisplayStatus) => {
    if (newStatus === TicketResolution.REJECTED) {
      setIsRejectModalOpen(true);
      return;
    }

    onStatusChange(statusToMutatePayload(newStatus));
  };

  const handleRejectConfirm = () => {
    onStatusChange(statusToMutatePayload(TicketResolution.REJECTED));
    setIsRejectModalOpen(false);
  };

  return (
    <>
      <Flex
        px={4}
        py={2}
        bg="gray.50"
        borderBottom="1px"
        borderColor="gray.200"
        align="center"
        justify="space-between"
        gap={4}
      >
        <Button
          leftIcon={<CheckCircle size={14} />}
          variant="outline"
          colorScheme="green"
          size="xs"
          onClick={onMarkResolved}
          isLoading={isLoading}
          isDisabled={ticket.resolution !== null}
        >
          <Trans>Mark as resolved</Trans>
        </Button>

        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
            <Trans>Status:</Trans>
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              rightIcon={<ChevronDown size={14} />}
              isLoading={isLoading}
              minW="120px"
            >
              {STATUS_LABELS[displayStatus]}
            </MenuButton>
            <MenuList>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem
                  key={status}
                  fontSize="sm"
                  onClick={() => handleStatusChange(status)}
                  bg={status === displayStatus ? 'bg.subtle' : undefined}
                  fontWeight={status === displayStatus ? 'semibold' : 'normal'}
                >
                  {STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <Modal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Mark as rejected</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Trans>
              This will close all attached chats. Are you sure you want to reject this ticket?
            </Trans>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsRejectModalOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button colorScheme="red" onClick={handleRejectConfirm} isLoading={isLoading}>
              <Trans>Reject ticket</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
